import { useState } from 'react';
import { cn } from '@/utils/cn';
import type { EconomyPhaseData, EomNarrative, EomPhaseNarrative } from '@/types/api';
import { SPEED_DIRECTION_ALIAS } from '@/types/api';

// ============================================================================
// Helpers
// ============================================================================

function scoreBadgeClass(score: number): string {
  if (score >= 80) return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30';
  if (score >= 60) return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
  return 'text-red-400 bg-red-400/10 border-red-400/30';
}

function scoreBarClass(score: number): string {
  if (score >= 80) return 'bg-emerald-400';
  if (score >= 60) return 'bg-amber-400';
  return 'bg-red-400';
}

function issueIcon(issueType: string): string {
  switch (issueType) {
    case 'direction': return '\u2194'; // ↔
    case 'magnitude': return '\u2195'; // ↕
    case 'both': return '\u21C4';      // ⇄
    case 'parasitic': return '\u26A0'; // ⚠
    default: return '\u2713';          // ✓
  }
}

function issueLabel(issueType: string): string {
  switch (issueType) {
    case 'direction': return 'Direction';
    case 'magnitude': return 'Speed';
    case 'both': return 'Dir + Speed';
    case 'parasitic': return 'Parasitic';
    default: return 'Good';
  }
}

function patternLabel(pattern: string): string {
  switch (pattern) {
    case 'direction_mismatch': return 'Path differs from reference';
    case 'tempo_mismatch': return 'Speed differs from reference';
    case 'mixed': return 'Path and speed differ';
    default: return pattern;
  }
}

/** Convert snake_case or space-separated name to Title Case */
function formatLandmarkName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ============================================================================
// Props
// ============================================================================

interface MovementEconomySectionProps {
  economyOfMotion: Record<string, EconomyPhaseData>;
  eomNarrative?: EomNarrative;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Displays a kinetic chain issue card */
function ChainIssueCard({ chain }: { chain: { display_name: string; pattern: string; avg_score: number; worst_landmark: string; worst_score: number; insight: string; landmarks: string[] } }) {
  return (
    <div className="bg-[#131920] border border-border-subtle rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary">{chain.display_name}</span>
        <span className={cn(
          'shrink-0 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border',
          scoreBadgeClass(chain.avg_score)
        )}>
          {Math.round(chain.avg_score)}
        </span>
      </div>
      <p className="text-xs text-text-muted">{patternLabel(chain.pattern)}</p>
      <p className="text-xs text-text-secondary leading-relaxed">{chain.insight}</p>
      <div className="flex flex-wrap gap-1 mt-1">
        {chain.landmarks.map((lm) => (
          <span key={lm} className="px-1.5 py-0.5 text-[10px] rounded bg-white/5 text-text-muted border border-white/5">
            {formatLandmarkName(lm)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Displays a worst-landmark card with issue type and insight */
function LandmarkInsightCard({ lm }: { lm: { name: string; score: number; issue_type: string; direction_score: number; magnitude_score: number; velocity_ratio: number | null; insight: string } }) {
  const directionPct = Math.round(lm.direction_score * 100);
  const magnitudePct = Math.round(lm.magnitude_score * 100);

  return (
    <div className="bg-[#131920] border border-border-subtle rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-text-muted shrink-0">{issueIcon(lm.issue_type)}</span>
          <span className="text-sm font-medium text-text-primary truncate">
            {formatLandmarkName(lm.name)}
          </span>
        </div>
        <span className={cn(
          'shrink-0 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border',
          scoreBadgeClass(lm.score)
        )}>
          {Math.round(lm.score)}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', scoreBarClass(lm.score))}
          style={{ width: `${Math.min(100, Math.max(0, lm.score))}%` }}
          role="progressbar"
          aria-valuenow={Math.round(lm.score)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Insight text */}
      <p className="text-xs text-text-secondary leading-relaxed">{lm.insight}</p>

      {/* Metrics row */}
      <div className="flex items-center gap-3 text-xs text-text-muted">
        <span className={cn(lm.issue_type === 'direction' || lm.issue_type === 'both' ? 'text-amber-400' : '')}>
          Dir: {directionPct}%
        </span>
        <span className="text-border-subtle">|</span>
        <span className={cn(lm.issue_type === 'magnitude' || lm.issue_type === 'both' ? 'text-amber-400' : '')}>
          Speed: {magnitudePct}%
        </span>
        {lm.velocity_ratio != null && (
          <>
            <span className="text-border-subtle">|</span>
            <span>{lm.velocity_ratio.toFixed(1)}x</span>
          </>
        )}
        <span className="text-border-subtle">|</span>
        <span className={cn(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          lm.issue_type === 'none' ? 'bg-emerald-400/10 text-emerald-400' : 'bg-amber-400/10 text-amber-400'
        )}>
          {issueLabel(lm.issue_type)}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Phase content — narrative-enhanced vs legacy
// ============================================================================

function NarrativePhaseContent({ narrative }: { narrative: EomPhaseNarrative }) {
  return (
    <div className="space-y-4">
      {/* Phase summary narrative */}
      <p className="text-sm text-text-secondary leading-relaxed">
        {narrative.summary}
      </p>

      {/* Chain issues */}
      {narrative.chain_issues.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Movement Chain Analysis
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {narrative.chain_issues.map((ci) => (
              <ChainIssueCard key={ci.chain} chain={ci} />
            ))}
          </div>
        </div>
      )}

      {/* Parasitic motion warnings */}
      {narrative.parasitic_landmarks.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Unnecessary Movement Detected
          </h4>
          {narrative.parasitic_landmarks.map((p) => (
            <div key={p.name} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-400/5 border border-red-400/15">
              <span className="text-red-400 text-xs mt-0.5">{issueIcon('parasitic')}</span>
              <p className="text-xs text-text-secondary">{p.insight}</p>
            </div>
          ))}
        </div>
      )}

      {/* Per-landmark detail cards */}
      {narrative.worst_landmarks.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Landmark Detail
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {narrative.worst_landmarks.map((lm, idx) => (
              <LandmarkInsightCard key={`${lm.name}-${idx}`} lm={lm} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LegacyPhaseContent({ data }: { data: EconomyPhaseData }) {
  const sortedLandmarks = [...(data.worst_landmarks ?? [])].sort(
    (a, b) => a.score - b.score
  );

  return (
    <div className="space-y-2">
      {sortedLandmarks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {sortedLandmarks.map((lm, idx) => {
            const directionPct = Math.round(lm.direction * 100);
            const magnitudePct = Math.round(lm.magnitude * 100);
            return (
              <div
                key={`${lm.name}-${idx}`}
                className="bg-[#131920] border border-border-subtle rounded-lg p-3 space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary truncate">
                    {formatLandmarkName(lm.name)}
                  </span>
                  <span className={cn(
                    'shrink-0 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border',
                    scoreBadgeClass(lm.score)
                  )}>
                    {Math.round(lm.score)}
                  </span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all', scoreBarClass(lm.score))}
                    style={{ width: `${Math.min(100, Math.max(0, lm.score))}%` }}
                    role="progressbar"
                    aria-valuenow={Math.round(lm.score)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <p className="text-xs text-text-muted">
                  Direction: {directionPct}%{' '}
                  <span className="text-border-subtle mx-1">|</span>
                  Magnitude: {magnitudePct}%
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-text-muted italic">No landmark detail available for this phase.</p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Movement Economy section for the Evaluation Results page.
 *
 * When eomNarrative is available, displays rich coaching narratives with
 * kinetic chain analysis and per-landmark insights. Falls back to the
 * legacy numerical-only display when narrative is not available.
 */
function MovementEconomySection({ economyOfMotion, eomNarrative }: MovementEconomySectionProps) {
  const phaseNames = eomNarrative
    ? Object.keys(eomNarrative.phases)
    : Object.keys(economyOfMotion);
  const [activePhase, setActivePhase] = useState<string>(phaseNames[0] ?? '');

  if (phaseNames.length === 0) return null;

  const narrativePhase = eomNarrative?.phases[activePhase];
  const legacyPhase = economyOfMotion[activePhase];
  const phaseScore = narrativePhase?.phase_score ?? legacyPhase?.phase_score ?? 0;

  return (
    <div className="space-y-4">
      {/* Headline narrative */}
      {eomNarrative && (
        <div className="px-3 py-2.5 rounded-lg bg-accent/5 border border-accent/15">
          <p className="text-sm text-text-primary leading-relaxed">
            {eomNarrative.headline}
          </p>
          {eomNarrative.overall_score != null && (
            <p className="text-xs text-text-muted mt-1">
              Overall economy: {Math.round(eomNarrative.overall_score)}/100
              {eomNarrative.economy_pattern !== 'efficient' && (
                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-400/10 text-amber-400 border border-amber-400/20">
                  {eomNarrative.economy_pattern.replace(/_/g, ' ')}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Phase tabs — only when multiple phases */}
      {phaseNames.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Economy phases">
          {phaseNames.map((name) => {
            const pScore = eomNarrative?.phases[name]?.phase_score ?? economyOfMotion[name]?.phase_score ?? 0;
            const isActive = name === activePhase;
            const badgeClass = scoreBadgeClass(pScore);
            return (
              <button
                key={name}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActivePhase(name)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-accent/20 border-accent text-accent'
                    : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:border-accent/50 hover:text-text-primary'
                )}
              >
                <span>{name}</span>
                <span className={cn(
                  'inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded border',
                  badgeClass
                )}>
                  {Math.round(pScore)}
                </span>
                {(() => {
                  const phaseIntent = eomNarrative?.phases[name]?.speed_intent;
                  if (!phaseIntent || phaseIntent === 'match') return null;
                  // Normalize old vocab (faster_ok/slower_ok/any) from not-yet-migrated templates.
                  const normalized = SPEED_DIRECTION_ALIAS[phaseIntent] ?? phaseIntent;
                  const label = normalized === 'reward_faster' ? 'Faster OK'
                    : normalized === 'reward_slower' ? 'Slower OK'
                    : 'Direction Only';
                  return (
                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-400/10 text-blue-400 border border-blue-400/20">
                      {label}
                    </span>
                  );
                })()}
              </button>
            );
          })}
        </div>
      )}

      {/* Phase score header */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <p className="text-xs text-text-muted">
            Phase economy score — {narrativePhase?.n_scored ?? legacyPhase?.n_scored ?? 0} landmark
            {(narrativePhase?.n_scored ?? legacyPhase?.n_scored ?? 0) !== 1 ? 's' : ''} evaluated
            {narrativePhase && narrativePhase.n_issues > 0 && (
              <span className="text-amber-400 ml-1">
                ({narrativePhase.n_issues} with issues)
              </span>
            )}
          </p>
          {narrativePhase?.speed_intent && narrativePhase.speed_intent !== 'match' && (
            <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded bg-blue-400/10 text-blue-400 border border-blue-400/20">
              {/* Normalize old vocab (faster_ok/slower_ok) from not-yet-migrated templates. */}
              {(SPEED_DIRECTION_ALIAS[narrativePhase.speed_intent] ?? narrativePhase.speed_intent) === 'reward_faster' ? 'Faster OK'
                : (SPEED_DIRECTION_ALIAS[narrativePhase.speed_intent] ?? narrativePhase.speed_intent) === 'reward_slower' ? 'Slower OK'
                : 'Direction Only'}
            </span>
          )}
        </div>
        <span
          className={cn(
            'text-2xl font-bold tabular-nums px-3 py-1 rounded-lg border',
            scoreBadgeClass(phaseScore)
          )}
        >
          {Math.round(phaseScore)}
        </span>
      </div>

      {/* Phase content — narrative or legacy */}
      {narrativePhase ? (
        <NarrativePhaseContent narrative={narrativePhase} />
      ) : legacyPhase ? (
        <LegacyPhaseContent data={legacyPhase} />
      ) : (
        <p className="text-xs text-text-muted italic">No economy data available for this phase.</p>
      )}
    </div>
  );
}

export default MovementEconomySection;
